import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { metaPixel } from '@/services/metaPixel';
import { trackPageView, trackCTA } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isChecking, setIsChecking] = useState(true);

  // Track scroll depth
  useScrollTracking('Welcome');

  // Meta Pixel & Google Analytics - Initialize and track
  useEffect(() => {
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('Welcome');

    // Check for purchase data from checkout and fire Purchase event
    const purchaseDataStr = sessionStorage.getItem('metaPixelPurchase');
    if (purchaseDataStr) {
      try {
        const purchaseData = JSON.parse(purchaseDataStr);
        metaPixel.purchase(purchaseData);
        // Clear the data so we don't fire again on refresh
        sessionStorage.removeItem('metaPixelPurchase');
      } catch (error) {
        console.error('Error parsing purchase data for Meta Pixel:', error);
      }
    }
  }, []);

  // Check authentication from localStorage (not Redux)
  useEffect(() => {
    const checkAuth = () => {
      try {
        const currentUser = authService.getCurrentUser();
        const isAuthenticated = authService.isAuthenticated();
        if (!currentUser || !isAuthenticated) {
          navigate('/sign-in', { replace: true });
        } else {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error checking auth on welcome page:', error);
        navigate('/sign-in', { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleGetStarted = () => {
    trackCTA.click('welcome-get-started', "Let's Get Started", 'welcome-page', '/dashboard');
    navigate('/dashboard');
  };

  // Show loading state while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-blue-950 to-emerald-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const firstName = user.first_name || user.username || 'there';

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-blue-950 to-emerald-950 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-4xl w-full">
          {/* Main welcome card */}
          <div className="text-center space-y-8 backdrop-blur-sm bg-slate-900/40 rounded-3xl p-12 md:p-16 border border-blue-500/20 shadow-2xl shadow-blue-500/10">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-emerald-500 to-blue-500 p-6 rounded-full">
                  <svg 
                    className="w-16 h-16 text-white" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2.5} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Welcome heading */}
            <div className="space-y-4">
              <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-blue-100 to-emerald-100 bg-clip-text text-transparent animate-fade-in">
                Welcome, {firstName}!
              </h1>
              <p className="text-2xl md:text-3xl text-blue-200 font-light">
                Your purchase is complete
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center justify-center gap-4 py-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
              <div className="w-2 h-2 rounded-full bg-blue-500/50"></div>
              <div className="h-px w-20 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
            </div>

            {/* Welcome message */}
            <div className="space-y-6 max-w-2xl mx-auto">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                Congratulations on taking this important step in your learning journey! 
                You now have full access to world-class content designed to transform your skills and accelerate your growth.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mt-8">
                <div className="bg-blue-500/10 backdrop-blur-sm rounded-xl p-6 border border-blue-500/20">
                  <div className="text-blue-400 mb-3">
                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Expert Content</h3>
                  <p className="text-gray-400 text-sm">Access premium lessons from industry leaders</p>
                </div>

                <div className="bg-emerald-500/10 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/20">
                  <div className="text-emerald-400 mb-3">
                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Learn at Your Pace</h3>
                  <p className="text-gray-400 text-sm">Lifetime access with no time limits</p>
                </div>

                <div className="bg-purple-500/10 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20">
                  <div className="text-purple-400 mb-3">
                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Track Progress</h3>
                  <p className="text-gray-400 text-sm">Monitor your growth with detailed analytics</p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-8">
              <button
                onClick={handleGetStarted}
                className="group relative inline-flex items-center gap-3 px-10 py-5 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 via-blue-500 to-emerald-600 rounded-full hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative">Let's Get Started</span>
                <svg 
                  className="relative w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>

            {/* Additional info */}
            <p className="text-sm text-gray-500 mt-8 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Your purchase confirmation has been sent to your email
            </p>
          </div>
        </div>
      </div>

      {/* Add fade-in animation */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .delay-1000 {
          animation-delay: 1s;
        }
        
        .delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </div>
  );
};

export default WelcomePage;
